/**
 * InlineEditor - Inline text editing for preview runtime
 *
 * Enables inline editing of text fields in preview mode by making elements
 * contenteditable and sending updates to the admin panel via sendToParent.
 *
 * Messages sent (flat, matching admin panel expectations):
 *   { type: 'field-edited', sectionId, field, value }
 *   { type: 'element-selected', sectionId, field, elementType: 'text', content }
 */

import type { MessageHandler } from './message-handler.js';

export class InlineEditor {
  private messageHandler: MessageHandler;
  private editableElements: Set<HTMLElement> = new Set();
  private imageElements: Set<HTMLImageElement> = new Set();
  private isViewOnly = false;
  private editabilityMap: Record<string, Record<string, boolean>> = {};

  constructor(messageHandler: MessageHandler) {
    this.messageHandler = messageHandler;
  }

  /**
   * Set view-only mode (disables all inline editing)
   */
  setViewOnly(viewOnly: boolean): void {
    this.isViewOnly = viewOnly;
  }

  /**
   * Set editability map (nested: sectionId -> field -> boolean)
   */
  setEditabilityMap(map: Record<string, Record<string, boolean>>): void {
    this.editabilityMap = map;
  }

  /**
   * Initialize inline editing on all data-dr-field text elements.
   * Called on init and after each full-update to catch new DOM nodes.
   */
  setupInlineEditing(): void {
    if (this.isViewOnly) return;

    const fields = document.querySelectorAll('[data-dr-field]');

    fields.forEach((el) => {
      const htmlEl = el as HTMLElement;
      const tagName = htmlEl.tagName.toLowerCase();

      // Skip non-text elements
      if (tagName === 'img' || tagName === 'video' || tagName === 'iframe' || tagName === 'input') return;

      // Skip elements inside data-dr-list-item (array items managed by panel)
      if (htmlEl.closest('[data-dr-list-item]')) return;

      // Check editability from map
      const sectionId = this.getSectionId(htmlEl);
      const field = htmlEl.getAttribute('data-dr-field');
      if (sectionId && field && this.editabilityMap[sectionId]?.[field] === false) return;

      // Skip if already set up
      if (htmlEl.getAttribute('data-dr-editable') === 'true') return;
      htmlEl.setAttribute('data-dr-editable', 'true');

      htmlEl.setAttribute('contenteditable', 'true');
      htmlEl.style.outline = 'none';
      htmlEl.style.cursor = 'text';

      this.editableElements.add(htmlEl);

      htmlEl.addEventListener('focus', () => {
        htmlEl.style.outline = '2px solid rgba(139, 92, 246, 0.5)';
        htmlEl.style.outlineOffset = '2px';
        htmlEl.style.borderRadius = '2px';

        const sid = this.getSectionId(htmlEl);
        const f = htmlEl.getAttribute('data-dr-field');
        if (sid && f) {
          this.messageHandler.sendToParent({
            type: 'element-selected',
            sectionId: sid,
            field: f,
            elementType: 'text',
            content: htmlEl.textContent || '',
          });
        }
      });

      htmlEl.addEventListener('blur', () => {
        htmlEl.style.outline = 'none';
        htmlEl.style.outlineOffset = '';
        htmlEl.style.borderRadius = '';

        const sid = this.getSectionId(htmlEl);
        const f = htmlEl.getAttribute('data-dr-field');
        if (sid && f) {
          this.messageHandler.sendToParent({
            type: 'field-edited',
            sectionId: sid,
            field: f,
            value: htmlEl.textContent || '',
          });
        }
      });

      htmlEl.addEventListener('input', () => {
        const sid = this.getSectionId(htmlEl);
        const f = htmlEl.getAttribute('data-dr-field');
        if (sid && f) {
          this.messageHandler.sendToParent({
            type: 'field-edited',
            sectionId: sid,
            field: f,
            value: htmlEl.textContent || '',
          });
        }
      });
    });
  }

  /**
   * Set up click handlers on all img[data-dr-field] elements.
   * Sends element-selected + image-replace-requested on click.
   */
  setupImageClickHandlers(): void {
    if (this.isViewOnly) return;

    const images = document.querySelectorAll('img[data-dr-field]');

    images.forEach((img) => {
      const htmlImg = img as HTMLImageElement;

      // Check editability
      const sectionId = this.getSectionId(htmlImg);
      const field = htmlImg.getAttribute('data-dr-field');
      if (sectionId && field && this.editabilityMap[sectionId]?.[field] === false) return;

      // Skip if already set up
      if (htmlImg.getAttribute('data-dr-img-handler') === 'true') return;
      htmlImg.setAttribute('data-dr-img-handler', 'true');

      htmlImg.style.cursor = 'pointer';
      htmlImg.style.transition = 'outline 0.15s ease';

      this.imageElements.add(htmlImg);

      htmlImg.addEventListener('mouseenter', () => {
        htmlImg.style.outline = '2px solid rgba(139, 92, 246, 0.5)';
        htmlImg.style.outlineOffset = '2px';
      });

      htmlImg.addEventListener('mouseleave', () => {
        htmlImg.style.outline = '';
        htmlImg.style.outlineOffset = '';
      });

      htmlImg.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const sid = this.getSectionId(htmlImg);
        const f = htmlImg.getAttribute('data-dr-field');

        if (sid && f) {
          this.messageHandler.sendToParent({
            type: 'element-selected',
            sectionId: sid,
            field: f,
            elementType: 'image',
            content: htmlImg.src,
          });

          this.messageHandler.sendToParent({
            type: 'image-replace-requested',
            sectionId: sid,
            field: f,
          });
        }
      });
    });
  }

  /**
   * Resolve the section ID from the nearest data-dr-section ancestor
   */
  private getSectionId(el: HTMLElement): string | null {
    const section = el.closest('[data-dr-section]');
    return section?.getAttribute('data-dr-section') || null;
  }

  destroy(): void {
    this.editableElements.clear();
    this.imageElements.clear();
  }
}
